import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  NamedLink,
  Modal,
} from '../../../components';

import HoverMenu from '../HoverMenu/HoverMenu';
import HoverMenuWide from './components/HoverMenuWide/HoverMenuWide';
import { DreamHomePageTab, FLOATING_TOPBAR, UserType } from '../../../util/enums';
import { apiBaseUrl } from '../../../util/api';
import Icons from '../../Icons/Icons';

import css from './TopbarDesktop.module.css';
import { getUserType } from '../../../util/dataExtractors';
import StatusModal from '../../StatusModal/StatusModal';
const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    isDreamHomeTopbar,
    isAuthenticationPage,
    onManageDisableScrolling,
    type,
  } = props;

  const [mounted, setMounted] = useState(false);
  const [hoverMenuOpened, setHoverMenuOpened] = useState(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const userType = getUserType(currentUser);
  const userRole = {
    isPropertyDeveloper: userType === UserType.PROPERTY_DEVELOPER,
    isArchitect: userType === UserType.ARCHITECT_PROFESSIONAL,
    isOffsite: userType === UserType.OFFSITE_SUPPLIER,
    isSelfBuilder: userType === UserType.SELF_BUILDER,
  };

  const isPropertyDeveloper = userRole.isPropertyDeveloper;
  const isArchitect = userRole.isArchitect;
  const isOffsiteOrArchitect = userRole.isArchitect || userRole.isOffsite;
  const isSelfBuilder = userRole.isSelfBuilder;
  const isCustomer = isSelfBuilder || isPropertyDeveloper;

  // Poll for unread supplier messages — customers only
  useEffect(() => {
    if (!isAuthenticated || !isCustomer) return;
    const sharetribeUserId = currentUser?.id?.uuid;
    if (!sharetribeUserId) return;

    const fetchUnread = () => {
      fetch(`${apiBaseUrl()}/api/leadflow/customer-threads?sharetribeUserId=${sharetribeUserId}`, {
        credentials: 'include',
      })
        .then(r => r.json())
        .then(data => {
          const total = (data.data || []).reduce((sum, t) => sum + (t.unreadCount || 0), 0);
          setUnreadMessageCount(total);
        })
        .catch(() => {});
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, isCustomer, currentUser?.id?.uuid]);

  const defaultRoot = type === FLOATING_TOPBAR ? css.root : css.staticRoot;

  const classes = classNames(rootClassName || defaultRoot, className, {
    [css.dreamHomeTopbarDesktop]: isDreamHomeTopbar,
    [css.hoverMenuOpened]: hoverMenuOpened,
    [css.authenticationTopbarDesktop]: isAuthenticationPage,
  });

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const profileMenu = authenticatedOnClientSide ? (
    <div
      className={css.profileMenuWrapper}
      onMouseEnter={() => setProfileMenuOpen(true)}
      onMouseLeave={() => setProfileMenuOpen(false)}
    >
      <div className={css.profileMenuLabel}>
        <Icons name="signupIcon" />
        <span className={css.userName}>
          {currentUser?.attributes?.profile?.displayName ||
           currentUser?.attributes?.profile?.firstName ||
           'User Name'}
        </span>
      </div>
      {profileMenuOpen && (
        <div className={css.profileMenuContent}>
          {isSelfBuilder ? null : (
            <div key="ManageListingsPage">
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
                name="ManageListingsPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.yourListingsLink" />
              </NamedLink>
            </div>
          )}

          <div key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.profileSettingsLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </div>
          {/* <div key="DreamHomePage">
            <NamedLink
              className={classNames(css.profileSettingsLink, currentPageClass('DreamHomePage'))}
              name="DreamHomePage"
              params={{ tab: DreamHomePageTab.PLOT_DETAILS }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.dreamHomePageLink" />
            </NamedLink>
          </div> */}
          {/* <div key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </div>
          <div key="CostEstimationDetailsPage">
            <NamedLink
              className={classNames(
                css.yourListingsLink,
                currentPageClass('CostEstimationDetailsPage')
              )}
              name="CostEstimationDetailsPage"
            >
              <span className={css.menuItemBorder} />
              <span>
                <FormattedMessage id="TopbarDesktop.costEstimationToolDetails" />
              </span>
            </NamedLink>
          </div>
          <div key="DocumentManagementPage">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('DocumentManagementPage'))}
              name="DocumentManagementPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.documentManagementPage" />
            </NamedLink>
          </div>
          <div key="MoodMapPage">
            <NamedLink
              className={classNames(css.yourListingsLink, currentPageClass('MoodMapPage'))}
              name="MoodMapPage"
            >
              <span className={css.menuItemBorder} />
              <span>Mood board</span>
            </NamedLink>
          </div> */}
          {isCustomer ? (
            <div key="MessagesPage">
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('InboxPage'))}
                name="InboxPage"
                params={{ tab: 'messages' }}
              >
                <span className={css.menuItemBorder} />
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  Messages
                  {unreadMessageCount > 0 && (
                    <span style={{ background: '#E8402A', color: '#fff', borderRadius: '10px', padding: '1px 6px', fontSize: '11px', fontWeight: 700 }}>
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                </span>
              </NamedLink>
            </div>
          ) : null}
          <div key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name={'LoginPage'} className={css.signupLink}>
      <Icons name="signupIcon" />
      <span className={css.signup}>
        {' '}
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  // LOGIN BUTTON - COMMENTED OUT BUT KEPT FOR FUTURE USE
  // const loginLink = isAuthenticatedOrJustHydrated ? null : (
  //   <NamedLink name={'LoginPage'} className={css.loginLink}>
  //     <Icons name="loginIcon" />
  //     <span className={css.signup}>
  //       {' '}
  //       <FormattedMessage id="TopbarDesktop.login" />
  //     </span>
  //   </NamedLink>
  // );
  // OLD HOVER MENUS - COMMENTED OUT FOR NEW DESIGN
  // const hoverMenus = [
  //   {
  //     label: intl.formatMessage({ id: 'TopbarDesktop.homeDesigns' }),
  //     links: [
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.viewHomeDesigns' }),
  //         name: 'SearchPage',
  //       },
  //       !isSelfBuilder && {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.uploadDesigns' }),
  //         name:
  //           (isPropertyDeveloper || isArchitect) && isAuthenticated
  //             ? 'NewListingPage'
  //             : 'SignupPage',
  //       },
  //     ],
  //   },
  //   {
  //     label: intl.formatMessage({ id: 'TopbarDesktop.trustedSuppliers' }),
  //     links: [
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.forMMCCompanies' }),
  //         name: 'PricingPage',
  //         hash: 'pricingMMC',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.forCustomers' }),
  //         name: 'PricingPage',
  //         hash: 'pricingCustomers',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.partnerships' }),
  //         name: 'PricingPage',
  //         hash: 'partnerships',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.faq' }),
  //         name: 'PricingPage',
  //         hash: 'faq',
  //       },
  //     ],
  //   },
  //   {
  //     label: intl.formatMessage({ id: 'TopbarDesktop.community' }),
  //     links: [
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.aboutUs' }),
  //         name: 'AboutUsPage',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.howItWorks' }),
  //         name: 'HowItWorksPage',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.discoverMMC' }),
  //         name: 'MMCPage',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'TopbarDesktop.blogs' }),
  //         name: 'BlogPage',
  //       },
  //     ],
  //   },
  //   {
  //     label: intl.formatMessage({ id: 'TopbarDesktop.about' }), // New About Us section
  //     links: [
  //       {
  //         label: intl.formatMessage({ id: 'HoverMenuWide.whoWeAre' }),
  //         name: 'AboutUsPage',
  //         hash: 'who-we-are',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'HoverMenuWide.blogs' }),
  //         name: 'BlogPage',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'HoverMenuWide.faqs' }),
  //         name: 'AboutUsPage',
  //         hash: 'faqs',
  //       },
  //       {
  //         label: intl.formatMessage({ id: 'HoverMenuWide.forInvestors' }),
  //         name: 'AboutUsPage',
  //         hash: 'investors',
  //       },
  //     ],
  //   },
  // ];

  // NEW MENU STRUCTURE - Simple 4 menu items
  const newMenuItems = [
    { label: 'LeadFlow', key: 'LeadFlow' },
    { label: 'Marketplace', key: 'Marketplace' },
    // { label: 'Trusted Suppliers', key: 'Trusted Suppliers' },
    { label: 'Resources', key: 'Resources' },
  ];

  return (
    <nav className={classes} onMouseLeave={() => setHoverMenuOpened(null)} onMouseEnter={() => {}}>
      <div className={css.menuLinks}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        />
        <div className={css.actionButtonsSection}>
          {/* OLD MENU RENDERING - COMMENTED OUT */}
          {/* {hoverMenus.map((menu, idx) => (
            <HoverMenu
              key={idx}
              label={menu.label}
              menuLinks={menu.links.filter(Boolean)}
              hoverMenuOpened={false}
              setHoverMenuOpened={setHoverMenuOpened}
            />
          ))} */}

          {/* NEW MENU RENDERING */}
          {newMenuItems.map((menu, idx) => (
            <div
              key={idx}
              className={css.menuItemWrapper}
              onMouseEnter={() => setHoverMenuOpened(menu.key)}
              onMouseLeave={() => setHoverMenuOpened(null)}
            >
              <button
                className={`${css.menuButton} ${hoverMenuOpened === menu.key ? css.menuButtonActive : ''}`}
                onClick={() => setHoverMenuOpened(hoverMenuOpened === menu.key ? null : menu.key)}
              >
                {menu.label}
              </button>
              {hoverMenuOpened === menu.key && (
                <HoverMenuWide
                  isAuthenticated={isAuthenticatedOrJustHydrated}
                  setHoverMenuOpened={setHoverMenuOpened}
                  hoverMenuOpened={hoverMenuOpened}
                  currentUser={currentUser}
                />
              )}
            </div>
          ))}
        </div>

        {isAuthenticated ? (
          isSelfBuilder ? (
            <NamedLink className={css.createListingLink} name="CustomerLandingPage">
              <span className={css.createListing}>
                {intl.formatMessage({ id: 'TopbarDesktop.getStarted' })}
              </span>
            </NamedLink>
          ) : isPropertyDeveloper ? (
            <NamedLink className={css.createListingLink} name="DeveloperLandingPage">
              <span className={css.createListing}>
                {intl.formatMessage({ id: 'TopbarDesktop.getStarted' })}
              </span>
            </NamedLink>
          ) : isOffsiteOrArchitect ? (
            <NamedLink className={css.createListingLink} name="NewListingPage">
              <span className={css.createListing}>
                {intl.formatMessage({ id: 'TopbarDesktop.addNewStudies' })}
              </span>
            </NamedLink>
          ) : null
        ) : null}

        {inboxLink}
        {profileMenu}
        {loginLink}
      </div>
      {/* OLD HOVER MENU RENDERING - MOVED TO INDIVIDUAL MENU ITEMS */}
      {/* {hoverMenuOpened && (
        <HoverMenuWide
          isAuthenticated={isAuthenticatedOrJustHydrated}
          setHoverMenuOpened={setHoverMenuOpened}
          hoverMenuOpened={hoverMenuOpened}
        />
      )} */}
      {/* <Modal
        isOpen={isOpen}
        onClose={() => {
          setOpen(false);
          console.log('Closing modal');
        }}
        onManageDisableScrolling={onManageDisableScrolling}
        isStatusModal={true}
      >
        <StatusModal isSuccessModal={false} isFailModal={true} />
      </Modal> */}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
